import { useCallback, useEffect, useRef, useState } from 'react';
import { api, urlDuFlux } from '../api/client';

/**
 * Le cœur du lecteur, sans une ligne d'interface.
 *
 * Ce qui est repris de `VideoPlayer.jsx` du site, à l'identique et
 * volontairement : les **règles**, pas les pixels. Une séance commencée dans
 * Discord doit se retrouver sur le site, et inversement — ce qui n'arrive que
 * si les deux écrivent la même progression, aux mêmes seuils.
 *
 * Les seuils viennent du site et n'ont pas été réinventés :
 *
 *   - en dessous de 30 s, on n'enregistre rien : personne ne veut « reprendre »
 *     un film ouvert par erreur ;
 *   - au-delà de 95 %, le film est fini — on efface la progression au lieu de
 *     proposer une reprise à trente secondes de la fin ;
 *   - la reprise n'est proposée qu'entre ces deux bornes.
 *
 * `VideoPlayer.jsx` n'est pas modifié. Ces règles y restent, et vivent ici en
 * double le temps que le site adopte ce module — ce sera son choix, pas une
 * contrainte imposée par l'Activity.
 */

const SECONDES_MINIMUM = 30;
const POURCENT_TERMINE = 95;
const INTERVALLE_SAUVEGARDE = 10_000;

export function useLecteur({ film, utilisateur }) {
  const videoRef = useRef(null);
  const [reprise, setReprise] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enLecture, setEnLecture] = useState(false);

  // Des refs et non des états : ces valeurs sont lues dans des gestionnaires
  // d'événements et un intervalle, qui capteraient sinon une version périmée.
  const dejaVuRef = useRef(false);
  const filmRef = useRef(film);
  filmRef.current = film;

  const source = film ? urlDuFlux(film.id) : null;

  /* ── Enregistrement de la progression ─────────────────────────────── */

  const enregistrer = useCallback(async () => {
    const video = videoRef.current;
    const courant = filmRef.current;

    if (!video || !courant?.id || !utilisateur?.id) return;

    const { currentTime, duration } = video;
    if (!duration || Number.isNaN(currentTime)) return;

    const pourcentage = (currentTime / duration) * 100;

    if (currentTime < SECONDES_MINIMUM || pourcentage > POURCENT_TERMINE) {
      // Terminé : la progression n'a plus de sens, elle proposerait de
      // reprendre à trente secondes du générique.
      if (pourcentage > POURCENT_TERMINE) {
        await api
          .post('/progress', { action: 'remove', user_id: utilisateur.id, movie_id: courant.id })
          .catch(() => {});
      }
      return;
    }

    await api
      .post('/progress', {
        action: 'save',
        user_id: utilisateur.id,
        movie_id: courant.id,
        movie_title: courant.title ?? '',
        position: currentTime,
        duration,
        percentage: pourcentage,
      })
      .catch(() => {
        // Une progression perdue n'interrompt pas une séance : on ne remonte
        // rien à l'écran, le prochain tour réessaiera.
      });
  }, [utilisateur?.id]);

  const marquerVu = useCallback(async () => {
    const courant = filmRef.current;
    if (dejaVuRef.current || !courant?.id || !utilisateur?.id) return;

    dejaVuRef.current = true;

    await api
      .post('/watched', {
        action: 'add',
        user_id: utilisateur.id,
        movie_id: courant.id,
        movie_title: courant.title ?? '',
      })
      .catch(() => {});
  }, [utilisateur?.id]);

  /* ── Reprise ──────────────────────────────────────────────────────── */

  useEffect(() => {
    setReprise(null);
    setErreur(null);
    dejaVuRef.current = false;

    if (!film?.id || !utilisateur?.id) return;

    let vivant = true;

    api
      .get(`/progress?user_id=${utilisateur.id}&movie_id=${encodeURIComponent(film.id)}`)
      .then((donnees) => {
        if (!vivant || !donnees?.progress) return;

        const { position, duration, percentage } = donnees.progress;
        if (percentage < POURCENT_TERMINE && position > SECONDES_MINIMUM) {
          setReprise({ position, duration, percentage });
        }
      })
      .catch(() => {});

    return () => { vivant = false; };
  }, [film?.id, utilisateur?.id]);

  /* ── Sauvegarde périodique et aux moments qui comptent ────────────── */

  useEffect(() => {
    if (!film?.id) return undefined;

    const minuteur = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) enregistrer();
    }, INTERVALLE_SAUVEGARDE);

    // Fermer l'Activity ou basculer d'onglet est le cas le plus courant, et
    // c'est celui qui perdrait la progression sans ça.
    const surMasquage = () => { if (document.hidden) enregistrer(); };
    document.addEventListener('visibilitychange', surMasquage);

    return () => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', surMasquage);
      enregistrer();
    };
  }, [film?.id, enregistrer]);

  /* ── Gestionnaires à poser sur l'élément vidéo ────────────────────── */

  const proprietes = {
    ref: videoRef,
    src: source ?? undefined,
    playsInline: true,
    controls: true,
    preload: 'metadata',
    onPlay: () => setEnLecture(true),
    onPause: () => { setEnLecture(false); enregistrer(); },
    onTimeUpdate: () => {
      const video = videoRef.current;
      if (!video?.duration) return;
      if ((video.currentTime / video.duration) * 100 >= POURCENT_TERMINE) marquerVu();
    },
    onError: () => setErreur('La lecture a échoué. Le fichier est peut-être indisponible.'),
  };

  const reprendre = useCallback(() => {
    const video = videoRef.current;
    if (!video || !reprise) return;

    video.currentTime = reprise.position;
    setReprise(null);
    video.play().catch(() => {});
  }, [reprise]);

  const recommencer = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = 0;
    setReprise(null);
    video.play().catch(() => {});
  }, []);

  return { proprietes, videoRef, reprise, reprendre, recommencer, erreur, enLecture };
}
